import {
  Questionnaire,
  QuestionnaireQuestionEntity,
  QuestionnaireQuestionGroupEntity,
  QuestionVisibility,
  RuleGroupKind,
  QuestionRuleGroupEntity,
} from "@Savus-Inc/questionnaire-types";
import { CarrierProductFilter, GroupQuestionMap, StateFilter } from "../DocumentationTypes/QuestionnaireTypes";
import { checkCarrierProducts } from "@Savus-Inc/questionnaire-ngn/dist/v1/core/generate-questionnaire-map";

const ruleGroupToString = (group: QuestionRuleGroupEntity): string => {
  return group.kind;
};

/**
 * Parses URL filters for carrier-product combinations.
 * Format: "Carrier1::Product1,Carrier2::Product2"
 */
export const parseCarrierProductsFromURL = (urlCarrierProducts: string | null): CarrierProductFilter[] => {
  if (!urlCarrierProducts) return [];
  try {
    return decodeURIComponent(urlCarrierProducts)
      .split(",")
      .map(cp => {
        const [carrier, product] = cp.split("::");
        return { carrier, product };
      })
      .filter(cp => cp.carrier && cp.product);
  } catch (e) {
    console.error("Failed to parse carrier-products from URL", e);
    return [];
  }
};

/**
 * Parses URL filters for states.
   @param {string | null} urlStates 
   @param {StateFilter[]} availableStates 
   @returns {StateFilter[]} 
 */
export const parseStatesFromURL = (urlStates: string | null, availableStates: StateFilter[]): StateFilter[] => {
  if (!urlStates) return [];
  try {
    const selectedShortNames = decodeURIComponent(urlStates).split(",");
    return availableStates.filter(state => selectedShortNames.includes(state.shortName));
  } catch (e) {
    console.error("Failed to parse states from URL", e);
    return [];
  }
};

/**
 * Computes the total number of questions per group.
   @param {QuestionnaireEntity} [questionnaire]
   @returns {GroupQuestionMap} 
 */
export const computeTotalQuestionsMap = (questionnaire?: Questionnaire): GroupQuestionMap => {
  if (!questionnaire) return {};
  return questionnaire.groups.reduce((acc: GroupQuestionMap, group: QuestionnaireQuestionGroupEntity) => {
    acc[group.id] = group.questions.map(q => q.question?.externalId).filter(Boolean) as string[];
    return acc;
  }, {} as GroupQuestionMap);
};

export const updateParam = (params: URLSearchParams, key: string, value: string | null) => {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
};

/**
 * Gets the filtered questions map.
 * @param {Questionnaire} currentQuestionnaire
 * @param {CarrierProductFilter[]} filterCarrierProducts
 * @param {string} searchTerm
 * @param {StateFilter[]} filterStates
 * @returns {GroupQuestionMap}
 */

export const getFilteredQuestionsMap = (
  currentQuestionnaire: Questionnaire,
  filterCarrierProducts: CarrierProductFilter[],
  searchTerm: string,
  filterStates: StateFilter[],
  isVisible: (rules: any[], filterStates: StateFilter[], filterCarrierProducts: CarrierProductFilter[]) => boolean,
): GroupQuestionMap => {
  const newFilteredQuestionsMap: GroupQuestionMap = {};

  currentQuestionnaire.groups.forEach(group => {
    newFilteredQuestionsMap[group.id] = [];
    group.questions.forEach(q => {
      const carrierProducts = filterCarrierProducts.map(v => {
        return {
          lineOfBusiness: {
            shortName: v.product,
          },
          carrier: {
            name: v.carrier,
          },
        } as never;
      });

      const matchesCarrierProducts =
        filterCarrierProducts.length === 0 || checkCarrierProducts(carrierProducts, q.showForCarriersAndProducts);

      const matchesSearch =
        !searchTerm ||
        q.question?.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.question?.externalId?.toLowerCase().includes(searchTerm.toLowerCase());
      console.log(carrierProducts, q.showForCarriersAndProducts, q.question?.externalId);

      if (matchesCarrierProducts && matchesSearch) {
        newFilteredQuestionsMap[group.id].push(q.question?.externalId as string);
      }
    });
  });

  return { ...newFilteredQuestionsMap };
};

export const getQuestionMap = (
  currentQuestionnaire: Questionnaire | null,
): Record<string, QuestionnaireQuestionEntity> => {
  if (!currentQuestionnaire) {
    return {};
  }
  return currentQuestionnaire.groups
    .flatMap(group => group.questions)
    .reduce(
      (acc, question) => {
        acc[question.question?.externalId || ""] = question;
        return acc;
      },
      {} as Record<string, QuestionnaireQuestionEntity>,
    );
};

export const getReferenceMap = (currentQuestionnaire: Questionnaire | null): Record<string, string> => {
  if (!currentQuestionnaire) {
    return {};
  }
  const { map } = calculateQuestionExternalIdMap(currentQuestionnaire as never);
  return map as Record<string, string>;
};

export const isVisible = (
  rules: QuestionRuleGroupEntity[],
  filterStates: StateFilter[],
  filterCarrierProducts: CarrierProductFilter[],
): boolean => {
  const displayRules = rules.map(group => ({
    rule: ruleGroupToString(group),
    action: group.actionKind as QuestionVisibility,
  }));

  const matchesStates = true;

  // Note: Carrier-product filtering is done at the question level using showForCarriersAndProducts
  // This function only checks visibility rules (state-based rules)
  return matchesStates;
};

function calculateQuestionExternalIdMap(currentQuestionnaire: Questionnaire): { map: any } {
  throw new Error("Function not implemented.");
}
